import { reactive } from 'vue';

/**
 * In-app replacements for confirm/prompt/alert. Native dialogs can be suppressed by the
 * browser or an embedding host (they then silently return "cancel"), so destructive actions
 * would never go through; these always show and match the rest of the UI.
 */
export interface DialogRequest {
  kind: 'confirm' | 'prompt' | 'alert';
  title?: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  value?: string;
  resolve: (v: string | boolean | null) => void;
}

export const dialogState = reactive<{ current: DialogRequest | null; queue: DialogRequest[] }>({ current: null, queue: [] });

function open(req: Omit<DialogRequest, 'resolve'>): Promise<string | boolean | null> {
  return new Promise((resolve) => {
    const r: DialogRequest = { ...req, resolve };
    if (dialogState.current) dialogState.queue.push(r);
    else dialogState.current = r;
  });
}

/** Called by the dialog component when the user answers. */
export function answer(v: string | boolean | null): void {
  const cur = dialogState.current;
  dialogState.current = dialogState.queue.shift() ?? null;
  cur?.resolve(v);
}

export async function ask(message: string, opts: { title?: string; okLabel?: string; cancelLabel?: string; danger?: boolean } = {}): Promise<boolean> {
  return (await open({ kind: 'confirm', message, ...opts })) === true;
}

export async function askText(message: string, value = '', opts: { title?: string; okLabel?: string } = {}): Promise<string | null> {
  const v = await open({ kind: 'prompt', message, value, ...opts });
  return typeof v === 'string' ? v : null;
}

export async function tell(message: string, opts: { title?: string } = {}): Promise<void> {
  await open({ kind: 'alert', message, ...opts });
}
